import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, LayoutGrid, BookOpen, ArrowLeft, Lock, User } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { supabase } from '../../utils/supabaseStorage';

interface NavItem {
  path: string;
  label: string;
  icon: typeof LayoutGrid;
}

const NAV: NavItem[] = [
  { path: '/studio', label: 'My agents', icon: LayoutGrid },
  { path: '/studio/profile', label: 'Public profile', icon: User },
  { path: '/studio/help', label: 'Guidelines', icon: BookOpen },
];

function isActive(pathname: string, path: string): boolean {
  const n = pathname.replace(/\/$/, '') || '/';
  if (path === '/studio') return n === '/studio' || n === '/studio/';
  return n === path || n.startsWith(path + '/');
}

export function StudioLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useAppStore((s) => s.session);
  const userRole = useAppStore((s) => s.userRole);
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
      window.location.href = '/login';
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-base">
      <header className="border-b border-subtle bg-surface z-30 shrink-0">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate('/studio')}
              className="flex items-center space-x-2 text-th-secondary hover:text-th-primary transition-colors p-2 hover:bg-overlay rounded-lg min-w-0"
            >
              <Sparkles className="w-5 h-5 text-brand shrink-0" />
              <div className="text-left min-w-0">
                <h1 className="text-lg font-bold text-th-primary leading-tight truncate">Creator Studio</h1>
                <p className="text-xs text-th-secondary leading-tight">Build, ship, share</p>
              </div>
            </button>

            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = isActive(location.pathname, item.path);
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      active
                        ? 'bg-brand text-surface'
                        : 'text-th-secondary hover:text-th-primary hover:bg-overlay'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 shrink-0">
              {session && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-elevated rounded-lg max-w-[180px]">
                  <User className="w-4 h-4 text-brand shrink-0" />
                  <div className="text-sm min-w-0 leading-tight">
                    <p className="text-th-primary font-medium truncate">{session.hy_name || 'Creator'}</p>
                    <p className="text-th-secondary text-xs">
                      {userRole === 'superadmin' ? 'Super Admin' : userRole === 'admin' ? 'Admin' : 'Creator'}
                    </p>
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate('/library')}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-subtle text-th-secondary hover:bg-overlay hover:text-th-primary"
                title="Back to the app store"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">Library</span>
              </button>
              <button
                type="button"
                onClick={() => void handleSignOut()}
                disabled={signingOut}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-status-error/15 border border-status-error/30 text-status-error hover:bg-status-error/25 disabled:opacity-50"
              >
                <Lock className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">{signingOut ? 'Signing out…' : 'Logout'}</span>
              </button>
            </div>
          </div>

          <nav className="md:hidden flex items-center gap-1 pt-1 overflow-x-auto">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = isActive(location.pathname, item.path);
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-brand text-surface'
                      : 'text-th-secondary hover:text-th-primary hover:bg-overlay'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0 relative">
        <Outlet />
      </div>

      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
}
